import { ElementRef } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

type RevealableForm = AbstractControl | null | undefined;
type FormHost = ElementRef<HTMLElement> | HTMLElement | null | undefined;

type InvalidFieldFinder = (host: HTMLElement) => HTMLElement | null;

const firstMatch =
  (selector: string): InvalidFieldFinder =>
  (host) =>
    host.querySelector<HTMLElement>(selector);

// Angular stamps its status classes on group containers (`<form [formGroup]>`, `formGroupName`) as well as
// on controls, and a container is ng-invalid whenever any control in it is — including a control with no
// rendered field. Landing on the container focuses whichever input comes first in it (KOR-15128), so a
// container is recognised by holding status-classed elements of its own, and skipped.
const FORM_STATUS_SELECTOR = '.ng-valid, .ng-invalid, .ng-pending';

const firstInvalidControl: InvalidFieldFinder = (host) =>
  Array.from(host.querySelectorAll<HTMLElement>('.ng-invalid')).find((element) => !element.querySelector(FORM_STATUS_SELECTOR)) ?? null;

/**
 * Finders that locate a field rendered in its error state, most specific first.
 * `.rkt-form-field.has-error` is what rocket-ui's field wrapper sets once Formly's
 * touched-gated `showError` flips; the rest are fallbacks for non-Formly controls.
 *
 * `.is-missing` covers the blockers that are not form fields at all — a document row still to
 * upload, an account row still to select (KOR-15024). Those screens carry no Formly fields, so the
 * two never compete for first place.
 */
const INVALID_FIELD_FINDERS: InvalidFieldFinder[] = [
  firstMatch('.rkt-form-field.has-error'),
  firstMatch('.is-missing'),
  firstMatch('[aria-invalid="true"]'),
  firstInvalidControl,
];

const FOCUSABLE_CONTROL_SELECTOR = ['input', 'select', 'textarea', 'button', '[contenteditable="true"]']
  .map((selector) => `${selector}:not([disabled])`)
  .join(', ');

const invalidControlPaths = (control: AbstractControl, path = ''): string[] =>
  control instanceof FormGroup || control instanceof FormArray
    ? Object.entries(control.controls).flatMap(([key, child]) => invalidControlPaths(child, path ? `${path}.${key}` : key))
    : control.invalid
      ? [path]
      : [];

// An invalid form with nothing on screen to fix is a dead end the customer cannot get out of, and it looks
// like nothing happened (KOR-15128). Name the controls responsible so it can be traced from the console.
const warnUnrenderedInvalidControls = (forms: RevealableForm[]): void => {
  const paths = forms.filter((form): form is AbstractControl => !!form).flatMap((form) => invalidControlPaths(form));
  if (paths.length) {
    console.warn('focusFirstInvalidField: the form is invalid but no invalid field is rendered', paths);
  }
};

const resolveHostElement = (host: FormHost): HTMLElement | null => {
  if (!host) return null;
  return host instanceof ElementRef ? host.nativeElement : host;
};

/**
 * Marks every control in the given forms as touched so already-registered validation
 * messages become visible, and reports whether the step may proceed.
 *
 * Mirrors the `form.invalid` semantics the step templates used to bind to: a form whose
 * controls are all disabled is not blocking, matching Angular's own form status.
 *
 * @returns `false` when any form is invalid — the caller should stop and not submit.
 */
export const revealInvalidFields = (...forms: RevealableForm[]): boolean =>
  forms
    .filter((form): form is AbstractControl => !!form)
    .reduce((canProceed, form) => {
      form.markAllAsTouched();
      return canProceed && !form.invalid;
    }, true);

/**
 * Scrolls the first field rendered in its error state into view and focuses its control,
 * so the user lands on the field they missed rather than hunting for it.
 *
 * Pass the forms that were checked to have it warn when one is invalid but none of its
 * invalid controls is rendered — the case focus cannot help with.
 */
export const focusFirstInvalidField = (host: FormHost, ...forms: RevealableForm[]): void => {
  const element = resolveHostElement(host);
  if (!element) return;

  const field = INVALID_FIELD_FINDERS.reduce<HTMLElement | null>((found, find) => found ?? find(element), null);
  if (!field) {
    warnUnrenderedInvalidControls(forms);
    return;
  }

  field.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const control = field.matches(FOCUSABLE_CONTROL_SELECTOR) ? field : field.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR);
  control?.focus({ preventScroll: true });
};
